import { useVideoCallContext } from "@/v5/contexts/VideoCallContext";
import { Button } from "@/v5/components/ui/button";
import { cn } from "@/v5/lib/utils";
import { useAnyModalOpen } from "@/components/ui/Modal";
import { useIsBelow } from "@/hooks/useBreakpoint";
import {
  Mic,
  MicOff,
  Phone,
  Video as VideoIcon,
  VideoOff,
  Monitor,
  Maximize2,
  Minimize2,
  Copy,
  Check,
  Mail,
  MessageSquare,
  AlertTriangle,
  RotateCcw,
  X,
} from "lucide-react";
import { useState, useCallback } from "react";
import { toast } from "@/store/toastStore";
import {
  VIDEO_INVITE_CHANNEL_LABEL,
  VIDEO_INVITE_CHANNELS,
  videoInviteReasonText,
  videoInviteSentText,
  type VideoInviteChannel,
} from "@/v5/lib/videoInvite";

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function ChannelIcon({ channel, className }: { channel: VideoInviteChannel; className?: string }) {
  return channel === "email" ? <Mail className={className} /> : <MessageSquare className={className} />;
}

/**
 * What actually happened to each invite, and a way to send another.
 *
 * There used to be a single "SMS invite sent" chip that appeared only on
 * success, so a patient with no mobile number on file produced a call screen
 * that explained nothing. Each channel now reports itself, and the copyable
 * join link below stays the fallback for every failure.
 */
function InviteStatus({ variant }: { variant: "dark" | "light" }) {
  const vc = useVideoCallContext();
  const dark = variant === "dark";

  const sendable = VIDEO_INVITE_CHANNELS.filter(
    (channel) => vc.inviteOptions?.[channel]?.available ?? true,
  );

  return (
    <div className="mt-2 space-y-1">
      {VIDEO_INVITE_CHANNELS.map((channel) => {
        const result = vc.inviteResults?.[channel];
        if (!result) return null;

        return (
          <p
            key={channel}
            className={cn(
              "text-xs flex items-center justify-center gap-1.5",
              result.sent
                ? dark ? "text-green-400" : "text-green-600"
                : dark ? "text-amber-300" : "text-amber-600",
            )}
          >
            {result.sent ? (
              <ChannelIcon channel={channel} className="h-3 w-3 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            )}
            {result.sent ? videoInviteSentText(channel) : videoInviteReasonText(channel, result.reason)}
          </p>
        );
      })}

      {sendable.length > 0 && (
        <div className="flex items-center justify-center gap-2 pt-1">
          {sendable.map((channel) => {
            const pending = vc.invitePending.includes(channel);
            const alreadySent = vc.inviteResults?.[channel]?.sent === true;
            const label = VIDEO_INVITE_CHANNEL_LABEL[channel];

            return (
              <Button
                key={channel}
                variant={dark ? "ghost" : "outline"}
                size="sm"
                disabled={pending}
                onClick={() => void vc.sendInvite([channel])}
                className={cn("h-7 text-xs", dark && "bg-white/10 hover:bg-white/20 text-white/80")}
              >
                <ChannelIcon channel={channel} className="mr-1.5 h-3 w-3" />
                {pending
                  ? "Sending…"
                  : alreadySent
                    ? `Send ${label} again`
                    : `Send ${label} invite`}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function FloatingVideoCall() {
  const vc = useVideoCallContext();

  // Step aside for an open modal, on the phone tier only. See FloatingNoteForm for the
  // full reasoning: z-[9999] corner chrome sits exactly on a phone sheet's action row,
  // Modal.tsx names these widgets as the case its registry exists for, and hiding beats
  // unmounting so a live call keeps its state. Desktop is untouched (R2).
  //
  // Two separate unconditional calls, never `useAnyModalOpen() && useIsBelow("md")` —
  // that short-circuits and changes this component's hook count when a modal opens
  // (React #310). FloatingNoteForm carries the full note.
  const anyModalOpen = useAnyModalOpen();
  const isPhone = useIsBelow("md");
  const stepAsideForModal = anyModalOpen && isPhone;
  const [copied, setCopied] = useState(false);

  const copyLink = useCallback(() => {
    if (!vc.joinUrl) return;
    navigator.clipboard.writeText(vc.joinUrl).then(() => {
      setCopied(true);
      toast.success("Join link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    });
  }, [vc.joinUrl]);

  if (vc.status === "idle") return null;

  const patientName = vc.activePatientName || "Patient";
  const initials = patientName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "PT";

  const isMinimized = vc.mode === "minimized";

  // ── Error states ──────────────────────────────────────────────────────
  if (vc.status === "error" && isMinimized) {
    return (
      <div className={cn("fixed bottom-6 right-6 z-[9999] w-[300px] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300", stepAsideForModal && "hidden")}>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0" />
            <span className="text-sm font-medium text-destructive truncate">Video Call Error</span>
            <div className="ml-auto flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={vc.expand}>
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={vc.dismissError}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {vc.errorMessage || "Something went wrong."}
          </p>
        </div>
      </div>
    );
  }

  if (vc.status === "error") {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-8">
          <div className="text-center space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-destructive" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg mb-2">Video Call Error</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {vc.errorMessage || "Something went wrong with the video call."}
              </p>
            </div>
            <InviteStatus variant="light" />
            {vc.joinUrl && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">Patient join link:</p>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-xs text-foreground w-full"
                >
                  {copied ? <Check className="h-3.5 w-3.5 flex-shrink-0 text-green-500" /> : <Copy className="h-3.5 w-3.5 flex-shrink-0" />}
                  <span className="truncate">{copied ? "Copied!" : vc.joinUrl}</span>
                </button>
              </div>
            )}
            <div className="flex gap-2 justify-center pt-2">
              {vc.activePatientId && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    vc.dismissError();
                    setTimeout(() => vc.joinRoom(vc.activePatientId!, vc.activePatientName ?? undefined), 50);
                  }}
                >
                  <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                  Try Again
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={vc.dismissError}>
                <X className="mr-1.5 h-3.5 w-3.5" />
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Active call (connecting / connected / reconnecting) ───────────────
  // The same DOM containers for localVideoRef and remoteVideoRef persist
  // across minimized/expanded modes — only the CSS wrapper changes — so
  // Twilio <video> elements attached inside them are never lost.

  return (
    <div
      className={cn(
        "fixed z-[9999] transition-all duration-300 ease-in-out",
        isMinimized
          ? "bottom-6 right-6 w-[300px] rounded-2xl bg-gray-900 border border-white/10 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in"
          : "inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in",
        // Only the minimized corner panel yields to a modal. The expanded variant is
        // `inset-0` and is the primary surface the user is looking at — hiding that
        // because something opened underneath it would be the opposite of the fix.
        isMinimized && stepAsideForModal && "hidden",
      )}
      onClick={isMinimized ? undefined : (e) => {
        if (e.target === e.currentTarget) vc.minimize();
      }}
    >
      <div
        className={cn(
          isMinimized
            ? "flex flex-col"
            : "w-[90vw] max-w-4xl h-[85vh] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200",
        )}
      >
        {/* Video area */}
        <div
          className={cn(
            "relative bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden",
            isMinimized ? "h-[160px]" : "flex-1 min-h-0",
          )}
        >
          {/* Remote video container (persistent) */}
          <div ref={vc.remoteVideoRef} className="absolute inset-0" />

          {/* Waiting placeholder */}
          {vc.remoteParticipantCount === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              {isMinimized ? (
                <p className="text-xs text-white/60">
                  {vc.status === "connecting" ? "Connecting..." : "Waiting for patient..."}
                </p>
              ) : (
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-4">
                    <span className="text-4xl font-display font-bold text-primary">
                      {initials}
                    </span>
                  </div>
                  <p className="text-lg font-display font-semibold text-white">{patientName}</p>
                  <p className="text-sm text-white/60 mt-1">
                    {vc.status === "connecting"
                      ? "Connecting..."
                      : vc.status === "reconnecting"
                        ? "Reconnecting..."
                        : "Waiting for patient to join..."}
                  </p>
                  <InviteStatus variant="dark" />
                  {vc.joinUrl && (
                    <button
                      onClick={copyLink}
                      className="mt-3 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-xs text-white/80 max-w-[260px] mx-auto"
                    >
                      {copied ? <Check className="h-3 w-3 flex-shrink-0 text-green-400" /> : <Copy className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate">{copied ? "Copied!" : vc.joinUrl}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Local video pip (persistent) */}
          <div
            className={cn(
              "absolute rounded-xl overflow-hidden border bg-card",
              isMinimized
                ? "top-2 right-2 w-[72px] h-[52px] rounded-lg border-white/20"
                : "top-4 right-4 w-40 h-28 card-soft-depth border-2 border-background",
            )}
          >
            <div
              ref={vc.localVideoRef}
              className={cn(
                "w-full h-full",
                isMinimized
                  ? "bg-gradient-to-br from-gray-700 to-gray-800"
                  : "bg-gradient-to-br from-muted to-muted/50",
              )}
            />
            {vc.isCameraOff && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <VideoOff className={cn(isMinimized ? "h-3 w-3" : "h-5 w-5", "text-white/60")} />
              </div>
            )}
          </div>

          {/* Duration badge */}
          <div
            className={cn(
              "absolute rounded-full backdrop-blur-sm",
              isMinimized ? "top-2 left-2 px-2 py-0.5 bg-black/60" : "top-4 left-4 px-3 py-1.5 bg-black/50",
            )}
          >
            <span className={cn("font-mono text-white", isMinimized ? "text-[10px]" : "text-xs")}>
              {vc.status === "connecting" ? "Connecting..." : formatDuration(vc.callDuration)}
            </span>
          </div>

          {/* Reconnecting indicator */}
          {vc.status === "reconnecting" && (
            <div
              className={cn(
                "absolute left-1/2 -translate-x-1/2 rounded-full bg-yellow-500/80 backdrop-blur-sm",
                isMinimized ? "top-2 px-2 py-0.5" : "top-4 px-3 py-1.5",
              )}
            >
              <span className={cn("font-medium text-white", isMinimized ? "text-[10px]" : "text-xs")}>
                Reconnecting...
              </span>
            </div>
          )}

          {/* Screen sharing indicator (expanded only) */}
          {vc.isScreenSharing && !isMinimized && (
            <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-blue-500/80 backdrop-blur-sm">
              <span className="text-xs font-medium text-white flex items-center gap-1.5">
                <Monitor className="h-3 w-3" /> Sharing screen
              </span>
            </div>
          )}

          {/* Minimize / Expand toggle (expanded only) */}
          {!isMinimized && (
            <Button
              variant="ghost"
              size="icon"
              onClick={vc.minimize}
              className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm"
              title="Minimize video call"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Controls */}
        {isMinimized ? (
          <div className="px-3 py-2.5 bg-gray-900 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/70 truncate max-w-[100px]">{patientName}</span>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={vc.toggleMute}
                  className={cn(
                    "h-8 w-8 rounded-full text-white",
                    vc.isMuted ? "bg-red-500/80 hover:bg-red-500" : "bg-white/10 hover:bg-white/20",
                  )}
                >
                  {vc.isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={vc.toggleCamera}
                  className={cn(
                    "h-8 w-8 rounded-full text-white",
                    vc.isCameraOff ? "bg-red-500/80 hover:bg-red-500" : "bg-white/10 hover:bg-white/20",
                  )}
                >
                  {vc.isCameraOff ? <VideoOff className="h-3.5 w-3.5" /> : <VideoIcon className="h-3.5 w-3.5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={vc.expand}
                  className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={vc.leaveRoom}
                  className="h-8 w-8 rounded-full bg-red-500 hover:bg-red-600 text-white"
                >
                  <Phone className="h-3.5 w-3.5 rotate-135" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 bg-card border-t border-border">
            <div className="flex items-center justify-center gap-3">
              <Button
                variant={vc.isMuted ? "destructive" : "outline"}
                size="icon"
                onClick={vc.toggleMute}
                className={cn(
                  "h-12 w-12 rounded-full transition-smooth",
                  !vc.isMuted && "hover:bg-primary/10 hover:text-primary hover:border-primary",
                )}
              >
                {vc.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
              </Button>

              <Button
                variant={vc.isCameraOff ? "destructive" : "outline"}
                size="icon"
                onClick={vc.toggleCamera}
                className={cn(
                  "h-12 w-12 rounded-full transition-smooth",
                  !vc.isCameraOff && "hover:bg-primary/10 hover:text-primary hover:border-primary",
                )}
              >
                {vc.isCameraOff ? <VideoOff className="h-5 w-5" /> : <VideoIcon className="h-5 w-5" />}
              </Button>

              <Button
                variant={vc.isScreenSharing ? "default" : "outline"}
                size="icon"
                onClick={vc.toggleScreenShare}
                className={cn(
                  "h-12 w-12 rounded-full transition-smooth",
                  !vc.isScreenSharing && "hover:bg-primary/10 hover:text-primary hover:border-primary",
                )}
              >
                <Monitor className="h-5 w-5" />
              </Button>

              <Button
                variant="destructive"
                size="icon"
                onClick={vc.leaveRoom}
                className="h-12 w-12 rounded-full bg-destructive hover:bg-destructive/90 transition-smooth"
              >
                <Phone className="h-5 w-5 rotate-135" />
              </Button>
            </div>

            <div className="mt-4 text-center">
              <p className="text-xs text-muted-foreground">
                Video call with {patientName}
              </p>
              {vc.remoteParticipantCount > 0 && (
                <p className="text-xs text-green-500 mt-1">
                  {vc.remoteParticipantCount} participant{vc.remoteParticipantCount > 1 ? "s" : ""} connected
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
